import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, Pizza, UserPlus, LogIn } from 'lucide-react';
import { toast } from 'sonner';
import PageSEO from '@/components/PageSEO';

const AdminLogin = () => {
  const navigate = useNavigate();
  const [isRegistering, setIsRegistering] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [registrationCode, setRegistrationCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [checkingSession, setCheckingSession] = useState(true);

  useEffect(() => {
    // Check if already logged in
    const checkSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        // Check if user is admin
        const { data: roles } = await supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', session.user.id)
          .eq('role', 'admin')
          .single();

        if (roles) {
          navigate('/admin');
          return;
        }
      }
      setCheckingSession(false);
    };

    checkSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_IN' && session) {
        // Check admin role
        setTimeout(async () => {
          const { data: roles } = await supabase
            .from('user_roles')
            .select('role')
            .eq('user_id', session.user.id)
            .eq('role', 'admin')
            .single();

          if (roles) {
            navigate('/admin');
          } else {
            toast.error('Accesso non autorizzato. Solo gli admin possono accedere.');
            await supabase.auth.signOut();
          }
        }, 0);
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast.error('Inserisci email e password');
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password,
      });

      if (error) {
        if (error.message.includes('Invalid login credentials')) {
          toast.error('Credenziali non valide');
        } else {
          toast.error(error.message);
        }
      }
    } catch (err: any) {
      toast.error('Errore durante il login');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !password || !confirmPassword || !registrationCode) {
      toast.error('Compila tutti i campi');
      return;
    }

    if (password !== confirmPassword) {
      toast.error('Le password non corrispondono');
      return;
    }

    if (password.length < 6) {
      toast.error('La password deve essere di almeno 6 caratteri');
      return;
    }

    setLoading(true);
    try {
      const response = await supabase.functions.invoke('register-admin', {
        body: {
          email: email.trim(),
          password,
          registrationCode: registrationCode.trim(),
        },
      });

      if (response.error) {
        toast.error(response.error.message || 'Errore durante la registrazione');
        return;
      }

      if (response.data?.error) {
        toast.error(response.data.error);
        return;
      }

      toast.success('Account creato! Ora puoi accedere.');
      setIsRegistering(false);
      setPassword('');
      setConfirmPassword('');
      setRegistrationCode('');
    } catch (err: any) {
      toast.error('Errore durante la registrazione');
    } finally {
      setLoading(false);
    }
  };

  if (checkingSession) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <PageSEO title="Accesso Operatori – Pizzeria La Torre" description="Area riservata agli operatori della Pizzeria La Torre." noindex />
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
            <Pizza className="h-8 w-8 text-primary" />
          </div>
          <h1 className="text-2xl font-bold text-foreground">
            {isRegistering ? 'Registrazione Admin' : 'Accesso Pizzeria'}
          </h1>
          <p className="text-muted-foreground mt-2">
            {isRegistering ? 'Crea un nuovo account operatore' : 'Area riservata agli operatori'}
          </p>
        </div>

        {isRegistering ? (
          <form onSubmit={handleRegister} className="card-elevated p-6 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="nuovo@pizzeria.it"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Conferma Password</Label>
              <Input
                id="confirmPassword"
                type="password"
                placeholder="••••••••"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="registrationCode">Codice di Registrazione</Label>
              <Input
                id="registrationCode"
                type="password"
                placeholder="Inserisci il codice segreto"
                value={registrationCode}
                onChange={(e) => setRegistrationCode(e.target.value)}
                required
              />
              <p className="text-xs text-muted-foreground">
                Richiedi il codice a un amministratore esistente
              </p>
            </div>

            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Registrazione...
                </>
              ) : (
                <>
                  <UserPlus className="h-4 w-4 mr-2" />
                  Registrati
                </>
              )}
            </Button>

            <div className="text-center">
              <button
                type="button"
                onClick={() => {
                  setIsRegistering(false);
                  setPassword('');
                  setConfirmPassword('');
                  setRegistrationCode('');
                }}
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                Hai già un account? Accedi
              </button>
            </div>
          </form>
        ) : (
          <form onSubmit={handleLogin} className="card-elevated p-6 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="admin@pizzeria.it"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>

            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Accesso...
                </>
              ) : (
                <>
                  <LogIn className="h-4 w-4 mr-2" />
                  Accedi
                </>
              )}
            </Button>

            <div className="text-center">
              <button
                type="button"
                onClick={() => setIsRegistering(true)}
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                Non hai un account? Registrati
              </button>
            </div>
          </form>
        )}

        <p className="text-center text-sm text-muted-foreground mt-6">
          <button
            onClick={() => navigate('/')}
            className="hover:text-foreground transition-colors"
          >
            ← Torna al sito
          </button>
        </p>
      </div>
    </div>
  );
};

export default AdminLogin;
